import axios from 'axios';

const redditApi = axios.create({
  baseURL: '/api/reddit',
  headers: { 'Content-Type': 'application/json' },
});

redditApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('reddit_access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// Prevent concurrent refresh attempts.
let _refreshPromise = null;

async function tryRefreshToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    const refresh = localStorage.getItem('reddit_refresh_token');
    if (!refresh) return false;
    try {
      // Plain axios (not redditApi) so this interceptor is not re-triggered.
      const { data } = await axios.post('/api/reddit/auth/token/refresh/', { refresh });
      if (data.access) {
        localStorage.setItem('reddit_access_token', data.access);
        return true;
      }
    } catch {
      // Refresh token expired/invalid — fall through to logout.
    }
    return false;
  })();
  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

function forceLogout() {
  localStorage.removeItem('reddit_access_token');
  localStorage.removeItem('reddit_refresh_token');
  window.location.assign('/reddit/login');
}

redditApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/token');

    if (
      status === 401 &&
      !isAuthEndpoint &&
      !error.config?._retried &&
      typeof window !== 'undefined'
    ) {
      const path = window.location.pathname || '';
      const isRedditPage = path.startsWith('/reddit') && !path.startsWith('/reddit/login');
      if (isRedditPage) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          const retryConfig = {
            ...error.config,
            _retried: true,
            headers: {
              ...error.config.headers,
              Authorization: `Bearer ${localStorage.getItem('reddit_access_token')}`,
            },
          };
          return redditApi(retryConfig);
        }
        forceLogout();
      }
    }
    return Promise.reject(error);
  },
);

export default redditApi;
